import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useOrderPanelStore } from '../../stores/order-panel-store'
import { OrderPanelHeader } from './OrderPanelHeader'
import { OrderComposer } from './OrderComposer'
import clsx from 'clsx'

export function OrderPanel() {
  const isOpen = useOrderPanelStore((s) => s.isOpen)
  const isSubmitting = useOrderPanelStore((s) => s.isSubmitting)
  const submitResult = useOrderPanelStore((s) => s.submitResult)
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const channel = useOrderPanelStore((s) => s.channel)
  const discountPercentage = useOrderPanelStore((s) => s.discountPercentage)
  const discountReason = useOrderPanelStore((s) => s.discountReason)
  const closePanel = useOrderPanelStore((s) => s.closePanel)
  const submitOrder = useOrderPanelStore((s) => s.submitOrder)

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, closePanel])

  // Auto-close after successful submit
  useEffect(() => {
    if (submitResult?.ok) {
      const timer = setTimeout(() => closePanel(), 3000)
      return () => clearTimeout(timer)
    }
  }, [submitResult?.ok, closePanel])

  const customer = useOrderPanelStore((s) => s.customer)
  // Een opdracht (ORDER) vereist altijd een telefoonnummer. In de drawer is er
  // geen klantformulier, dus blokkeer de submit en verwijs naar de klantkaart
  // (een offerte mag wél zonder telefoon).
  const orderNeedsPhone = channel === 'ORDER' && !customer?.phone?.trim()

  // Can submit?
  const canSubmit = bootstrap
    && !isSubmitting
    && !submitResult?.ok
    && (discountPercentage === 0 || discountReason.length > 0)
    && !orderNeedsPhone

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={closePanel}
      />

      {/* Drawer */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full w-[480px] max-w-full bg-white shadow-2xl z-50 flex flex-col',
          'transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <OrderPanelHeader />

        {/* Success state */}
        {submitResult?.ok && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1">
              {channel === 'ORDER' ? 'Order aangemaakt' : 'Offerte aangemaakt'}
            </h3>
            <p className="text-sm text-slate-600">
              {submitResult.leadnummer && (
                <span className="font-medium">{submitResult.leadnummer}</span>
              )}
            </p>
            {submitResult.workOrderId ? (
              <Link
                to={`/orders/${submitResult.workOrderId}`}
                onClick={closePanel}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Open order {submitResult.workOrderNumber || ''} →
              </Link>
            ) : channel === 'QUOTE' && submitResult.leadId ? (
              <Link
                to={`/offertes/${submitResult.leadId}`}
                onClick={closePanel}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
              >
                Open offerte →
              </Link>
            ) : null}
            <button
              onClick={closePanel}
              className="mt-4 px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700"
            >
              Sluiten
            </button>
          </div>
        )}

        {/* Error state */}
        {submitResult && !submitResult.ok && (
          <div className="mx-5 mt-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {submitResult.error || 'Er ging iets mis bij het aanmaken'}
          </div>
        )}

        {/* Content */}
        {!submitResult?.ok && (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <OrderComposer variant="drawer" />
            </div>

            {/* Footer */}
            {bootstrap && (
              <div className="px-5 py-4 border-t border-slate-200 bg-white">
                {orderNeedsPhone && (
                  <p className="mb-3 px-3 py-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded">
                    Een opdracht vereist een telefoonnummer. Deze klant heeft er geen —
                    vul er eerst één in op de klantkaart, of maak in plaats daarvan een offerte aan.
                  </p>
                )}
                <div className="flex gap-3">
                <button
                  onClick={closePanel}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={() => submitOrder()}
                  disabled={!canSubmit}
                  className={clsx(
                    'flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                    canSubmit
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Bezig...
                    </span>
                  ) : (
                    channel === 'ORDER' ? 'Order aanmaken' : 'Offerte aanmaken'
                  )}
                </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
